import { ResultContainer, ResultContainerOuter, SearchDialogContent } from '@atlassian/search-dialog';
import React, { useCallback, useMemo } from 'react';
import { LoadingSpinner } from '../../../../../common/loading-spinner';
import { ProductStates } from '../../../../product-state-machine/product-state-machine-types';
import { GenericSearchResult } from '../../../../search-result/search-result';
import { messages } from '../../../../../messages';
import { useCurrentProductHasFilters } from '../../../../filters/filters-helper-functions';
import { NoPreQueryResults, NoSearchResults, PreQueryErrorScreen } from '../../../../no-results-screen';
import { useQuery } from '../../../../query-context';
import { SearchErrorScreen } from '../../../../search-error-screen/search-error-screen';
import { HeightRetainingContainer } from '../height-retaining-container';
import { GetDefaultFooter } from './default-footer';
import { FilterSection } from './filter-section';
import { useAutocorrection } from '../../../../autocorrection/autocorrection-context';
import { useAutocorrectionEnabled } from '../../../../autocorrection/autocorrection-helper-functions';
import { useIsMobileView } from '../../../../utils/use-is-mobile-view';
import { hasSearchResults } from '../../utils';
import { useShowAtlassianHomeSearchLink } from '../../../../../common/show-atlassian-home-search-link/show-atlassian-home-search-link';
import { useProductContext } from '../../../product-router';
export var ResultRenderer = function ResultRenderer(_ref) {
  var productState = _ref.productState,
    preQueryItems = _ref.preQueryItems,
    postQueryItems = _ref.postQueryItems,
    onRetryPreQuery = _ref.onRetryPreQuery,
    onRetryPostQuery = _ref.onRetryPostQuery,
    _ref$urlGeneratorForN = _ref.urlGeneratorForNoResultsScreen,
    urlGeneratorForNoResultsScreen = _ref$urlGeneratorForN === void 0 ? function () {
      return '';
    } : _ref$urlGeneratorForN,
    preQuerySectionTitleGenerator = _ref.preQuerySectionTitleGenerator,
    linkComponent = _ref.linkComponent,
    children = _ref.children,
    searchFooterLabel = _ref.searchFooterLabel,
    appendQueryParams = _ref.appendQueryParams,
    isPreQueryFiltersEnabled = _ref.isPreQueryFiltersEnabled;
  var DefaultHeader = function DefaultHeader() {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  };
  var DefaultSectionFooter = function DefaultSectionFooter() {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  };
  var isMobileView = useIsMobileView();
  var areFiltersEnabled = useCurrentProductHasFilters();
  var _useQuery = useQuery(),
    query = _useQuery.query;
  var _useAutocorrectionEna = useAutocorrectionEnabled(),
    autocorrectionEnabled = _useAutocorrectionEna.autocorrectionEnabled;
  var _useAutocorrection = useAutocorrection(),
    correction = _useAutocorrection.correction;
  var renderer = children && typeof children === 'function' ? children({
    query: query,
    productState: productState,
    linkComponent: linkComponent,
    preQueryItems: preQueryItems,
    postQueryItems: postQueryItems,
    onRetryPreQuery: onRetryPreQuery,
    onRetryPostQuery: onRetryPostQuery,
    urlGeneratorForNoResultsScreen: urlGeneratorForNoResultsScreen,
    preQuerySectionTitleGenerator: preQuerySectionTitleGenerator
  }) : null;
  var Header = (renderer === null || renderer === void 0 ? void 0 : renderer.Header) || DefaultHeader;
  var SectionFooter = (renderer === null || renderer === void 0 ? void 0 : renderer.SectionFooter) || DefaultSectionFooter;
  var Footer = (renderer === null || renderer === void 0 ? void 0 : renderer.Footer) || /*#__PURE__*/React.createElement(GetDefaultFooter, {
    urlGeneratorForNoResultsScreen: urlGeneratorForNoResultsScreen,
    linkComponent: linkComponent,
    productState: productState,
    searchFooterLabel: searchFooterLabel
  });
  var renderResults = useCallback(function () {
    switch (productState) {
      case ProductStates.PostQueryError:
        return /*#__PURE__*/React.createElement(SearchErrorScreen, {
          onRetry: onRetryPostQuery
        });
      case ProductStates.PostQueryTyping:
        return /*#__PURE__*/React.createElement(GenericSearchResult, {
          searchItems: postQueryItems,
          screen: "cachedResults",
          forceHideResultCount: true,
          linkComponent: linkComponent,
          isCollapsed: areFiltersEnabled || isMobileView,
          productState: ProductStates.PostQueryTyping,
          appendQueryParams: appendQueryParams
        });
      case ProductStates.PostQueryLoading:
        return /*#__PURE__*/React.createElement(GenericSearchResult, {
          searchItems: postQueryItems,
          screen: "cachedResults",
          forceHideResultCount: true,
          linkComponent: linkComponent,
          isCollapsed: areFiltersEnabled || isMobileView,
          productState: ProductStates.PostQueryLoading,
          appendQueryParams: appendQueryParams
        }, /*#__PURE__*/React.createElement(LoadingSpinner, null));
      case ProductStates.PostQueryNoResult:
        return hasSearchResults(postQueryItems) ? /*#__PURE__*/React.createElement(GenericSearchResult, {
          searchItems: postQueryItems,
          screen: "cachedResults",
          forceHideResultCount: true,
          linkComponent: linkComponent,
          isCollapsed: areFiltersEnabled || isMobileView,
          productState: ProductStates.PostQueryLoading,
          appendQueryParams: appendQueryParams
        }) : /*#__PURE__*/React.createElement(NoSearchResults, {
          urlGeneratorForNoResultsScreen: urlGeneratorForNoResultsScreen
        });
      case ProductStates.PostQuerySuccess:
        return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GenericSearchResult, {
          searchItems: postQueryItems,
          forceHideResultCount: autocorrectionEnabled && !!correction && correction.length > 0,
          linkComponent: linkComponent,
          screen: "postQuerySearchResults",
          productState: ProductStates.PostQuerySuccess,
          isCollapsed: areFiltersEnabled || isMobileView,
          renderSectionViewAll: true,
          appendQueryParams: appendQueryParams
        }));
      case ProductStates.PreQueryError:
        return /*#__PURE__*/React.createElement(PreQueryErrorScreen, null);
      case ProductStates.PreQueryLoading:
        return /*#__PURE__*/React.createElement(LoadingSpinner, null);
      case ProductStates.PreQueryNoResult:
        return /*#__PURE__*/React.createElement(NoPreQueryResults, null);
      case ProductStates.PreQuerySuccess:
        return /*#__PURE__*/React.createElement(GenericSearchResult, {
          searchItems: preQueryItems,
          generateSectionTitle: function generateSectionTitle(section, intl) {
            return (preQuerySectionTitleGenerator === null || preQuerySectionTitleGenerator === void 0 ? void 0 : preQuerySectionTitleGenerator(section)) || intl.formatMessage(messages.recently_viewed_title, {
              sectionTitle: section.title
            });
          },
          linkComponent: linkComponent,
          screen: "preQuerySearchResults",
          forceHideResultCount: true,
          productState: ProductStates.PreQuerySuccess,
          isCollapsed: isMobileView,
          appendQueryParams: appendQueryParams
        });
    }
  }, [correction, autocorrectionEnabled, productState, preQueryItems, postQueryItems, linkComponent, onRetryPostQuery, preQuerySectionTitleGenerator, urlGeneratorForNoResultsScreen, areFiltersEnabled, isMobileView, appendQueryParams]);
  var body = useMemo(function () {
    return renderer && renderer.Body && (renderer === null || renderer === void 0 ? void 0 : renderer.Body()) !== undefined ? renderer.Body : renderResults;
  }, [renderer, renderResults]);
  var isOnLoadingScreen = productState === ProductStates.PostQueryLoading || productState === ProductStates.PreQueryLoading;
  var footer = typeof Footer === 'function' ? /*#__PURE__*/React.createElement(Footer, null) : Footer;
  var _useProductContext = useProductContext(),
    getPrimaryProduct = _useProductContext.getPrimaryProduct;
  var primaryProduct = getPrimaryProduct();
  var showAtlassianHomeSearchLink = useShowAtlassianHomeSearchLink(primaryProduct === null || primaryProduct === void 0 ? void 0 : primaryProduct.id);
  return /*#__PURE__*/React.createElement(HeightRetainingContainer, {
    retainHeight: isOnLoadingScreen
  }, /*#__PURE__*/React.createElement(Header, null), /*#__PURE__*/React.createElement(SearchDialogContent, {
    showAtlassianHomeSearchLink: showAtlassianHomeSearchLink
  }, /*#__PURE__*/React.createElement(ResultContainerOuter, {
    "aria-live": "polite"
  }, /*#__PURE__*/React.createElement(ResultContainer, null, body()), /*#__PURE__*/React.createElement(SectionFooter, null)), areFiltersEnabled && /*#__PURE__*/React.createElement(FilterSection, {
    productState: productState,
    isPreQueryFiltersEnabled: isPreQueryFiltersEnabled
  })), footer);
};